import * as React from 'react';
import { useHistory } from 'react-router';
import jwtDecode from 'jwt-decode';
import { useGraphForm } from 'src/shared/forms/use-graph-form';
import { useTokenContext } from 'src/account/shared/service/token.context';
import { RecoverPasswordForm } from 'src/account/recover-access/service/recover-password.form';
import { ResetPasswordForm } from 'src/account/recover-access/service/reset-password.form';
import { useSearchParams } from 'src/shared/hooks/use-search-params';
import { toast } from 'react-toastify';

const getResetPasswordLinkExpiredError = (expirationHours) => (
  <div className="flex tw-flex-col">
    <p className="tw-font-semibold tw-text-sm">Reset Password Link Expired</p>
    <span className="tw-text-sm">
      Please request a new password reset link and use it within{' '}
      {expirationHours} hours.
    </span>
  </div>
);

export const useRecoverPassword = () => {
  const [finished, setFinished] = React.useState(false);

  const form = useGraphForm(RecoverPasswordForm, {
    onSuccess() {
      setFinished(true);
    },
  });

  return { form, finished };
};

export const useResetPassword = () => {
  const history = useHistory();
  const [params] = useSearchParams();
  const { saveToken } = useTokenContext();

  React.useEffect(() => {
    if (params.token) {
      try {
        const decodedToken = jwtDecode(params.token);
        const currentTime = Date.now() / 1000;

        const totalValidHours = Math.ceil(
          (decodedToken.exp - decodedToken.iat) / 3600,
        );

        if (decodedToken.exp && decodedToken.exp < currentTime) {
          toast.error(getResetPasswordLinkExpiredError(totalValidHours));
          history.push('/recover-password');
          return;
        }

        saveToken({ accessToken: params.token });
      } catch (error) {
        toast.error(getResetPasswordLinkExpiredError(4));
        history.push('/recover-password');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.token]);

  return useGraphForm(ResetPasswordForm, {
    onSuccess() {
      toast.success('Password reset successfully');
      history.push('/login');
    },
    watchErrors: true,
    disableDefaultErrorToast: true,
    onError: () => {
      toast.error(getResetPasswordLinkExpiredError(4));
      history.push('/recover-password');
    },
  });
};
